import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getListingDetailsPublicAndUser } from "../../actions/reduxActions/listingActions";
import {
  increaseListingViewCard,
  increaseListingViewMap,
} from "../../actions/regListingActions";
import {
  CONTACT_RESET,
  CONTACT_CLEAR_ERRORS,
} from "../../constants/contactConstants";
import {
  CLEAR_ERRORS_LISTING_DETAILS,
  USER_LISTING_DETAILS_CLEANUP_STATE,
} from "../../constants/listingConstants";
import { contact } from "../../actions/reduxActions/contactActions";
import { toast } from "sonner";
import { Modal, Alert, Spinner } from "react-bootstrap";
import PropertyImagesCarousel from "../Cards/PropertyImagesCarousel";
import ListingInfoPanel from "../Listing/ListingInfoPanel";
import { usePageMeta } from "../../lib/usePageMeta";
import getVideoId from "get-video-id";
import { useTranslation } from "react-i18next";
import NotFound from "../../pages/notFoundPages/NotFound";
import { ListingDetailsSkeleton } from "../ui/Skeleton";

const smartTrim = (str, length, delim, appendix) => {
  if (str.length <= length) return str;
  let trimmed = str.substr(0, length + delim.length);
  const idx = trimmed.lastIndexOf(delim);
  if (idx >= 0) trimmed = trimmed.substr(0, idx);
  if (trimmed) trimmed += appendix;
  return trimmed;
};

const ListingDetailsPage = ({ termsContact, termsContactUrdu }) => {
  const navigate = useNavigate();
  const history = useMemo(
    () => ({
      push: (to) => navigate(to),
      replace: (to) => navigate(to, { replace: true }),
      goBack: () => navigate(-1),
    }),
    [navigate]
  );
  const __params__ = useParams();
  const match = useMemo(() => ({ params: __params__ }), [__params__]);

  const [t, i18n] = useTranslation("common");
  const rtl = i18n.languages[0] === "urdu" ? "text-end" : "";

  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.user);
  const userId = user && user._id;
  const { click } = useSelector((state) => state.increaseListingViewCount);

  const { loading, error, listing } = useSelector(
    (state) => state.listingDetails
  );

  const {
    loading: contactLoading,
    error: contactError,
    success: contactSuccess,
  } = useSelector((state) => state.contact);

  const [rf, setRf] = useState(true);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsApp, setWhatsApp] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [videoId, setVideoId] = useState("");
  const [showContact, setShowContact] = useState(false);
  const [moveContent, setMoveContent] = useState(false);

  useEffect(() => {
    if (contactSuccess) {
      dispatch({ type: CONTACT_CLEAR_ERRORS });
      setName("");
      setPhone("");
      setWhatsApp("");
      setEmail("");
      setMessage(
        `I am interested in ${listing.title && listing.title}, \nAt: ${
          listing.address && listing.address
        } \nID: ${listing.uid && listing.uid}`
      );
      setTimeout(() => dispatch({ type: CONTACT_RESET }), 8000);
    }
  }, [dispatch, contactSuccess, listing.uid, listing.title, listing.address]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch({ type: CLEAR_ERRORS_LISTING_DETAILS });
    }
  }, [dispatch, error]);

  useEffect(() => {
    if (listing && listing.videoUrl && listing.videoUrl !== "") {
      const { id } = getVideoId(listing.videoUrl);
      setVideoId(id);
    }
  }, [listing]);

  useEffect(() => {
    if (match.params.id) {
      dispatch(getListingDetailsPublicAndUser(match.params.id));
      setRf(false);
    } else {
      toast.error("Property details not found");
      setRf(false);
    }
    return () => dispatch({ type: USER_LISTING_DETAILS_CLEANUP_STATE });
  }, [dispatch, match.params.id]);

  const isMounted = useRef();
  useEffect(() => {
    isMounted.current = true;
    if (isMounted.current && listing && Object.keys(listing).length) {
      if (click === "map") {
        increaseListingViewMap(listing._id, listing.uid && listing.uid, userId);
      } else {
        increaseListingViewCard(
          listing._id,
          listing.uid && listing.uid,
          userId
        );
      }
    }
    return () => {
      isMounted.current = false;
    };
  }, [click, userId, listing]);

  const handleCloseContact = () => setShowContact(false);
  const handleShowContact = () => {
    setMessage(
      `I am interested in ${listing.title && listing.title}, \nAt: ${
        listing.address && listing.address
      } \nID: ${listing.uid && listing.uid}`
    );
    setShowContact(true);
  };

  const handleChange = (e) => {
    if (e.target.name === "name") {
      setName(e.target.value);
    } else if (e.target.name === "phone") {
      const x = e.target.value.replace(/\D/g, "").match(/(\d{0,4})(\d{0,9})/);
      setPhone(!x[2] ? x[1] : x[1] + "-" + x[2] + (x[3] ? "-" + x[3] : ""));
    } else if (e.target.name === "email") {
      setEmail(e.target.value);
    } else if (e.target.name === "message") {
      setMessage(e.target.value);
    } else if (e.target.name === "whatsApp") {
      const w = e.target.value.replace(/\D/g, "").match(/(\d{0,4})(\d{0,9})/);
      setWhatsApp(!w[2] ? w[1] : w[1] + "-" + w[2] + (w[3] ? "-" + w[3] : ""));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.set("name", name);
    formData.set("phone", phone);
    formData.set("whatsApp", whatsApp);
    formData.set("email", email);
    formData.set("message", message);
    dispatch(contact(formData));
  };

  // usePageMeta must run on every render (Rules of Hooks). It used to sit below
  // the `loading || rf` early return, so on a cold/direct URL load the first
  // renders skipped it and the post-fetch render called 4 more hooks than the
  // previous one — React error #310, blank page. The ternaries already guard an
  // empty `listing`, so it's safe to call before the early returns.
  usePageMeta({
    title:
      listing && listing.title
        ? listing.title + " | PkPlots"
        : "PkPlots | Property Listing",
    description:
      listing && listing.description
        ? smartTrim(listing.description, 250, "", "...")
        : "Property description",
    keywords: listing && listing.address ? listing.address : "Property address",
  });

  if (loading || rf) return <ListingDetailsSkeleton />;

  if (Object.keys(listing).length <= 1) return <NotFound t={t} />;

  const contactSlot = (
    <>
      <button
        type='button'
        className='contact-modal__submit'
        onClick={handleShowContact}
      >
        <i className='bx bxs-envelope'></i>
        {t("viewPropDetails.contact")}
      </button>

      <Modal
        backdropClassName='forBackdrop'
        dialogClassName='contact-modal'
        show={showContact}
        onHide={handleCloseContact}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("viewPropDetails.contact")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='contact-modal__subtitle'>
            {listing.title || listing.address || ""}
          </p>
          <form onSubmit={handleSubmit}>
            {contactSuccess && (
              <Alert variant='success' className='mb-3'>
                {t("viewPropDetails.sent")}
              </Alert>
            )}
            {contactError && (
              <Alert
                variant='danger'
                className='mb-3'
                onClose={() => dispatch({ type: CONTACT_CLEAR_ERRORS })}
                dismissible
              >
                {contactError}
              </Alert>
            )}
            <div className='contact-modal__group'>
              <label className='contact-modal__label' htmlFor='cm-name'>
                {t("viewPropDetails.name")}
              </label>
              <div className='contact-modal__input'>
                <input
                  id='cm-name'
                  required
                  type='text'
                  name='name'
                  value={name}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className='contact-modal__group'>
              <label className='contact-modal__label' htmlFor='cm-phone'>
                {t("viewPropDetails.phone")}
              </label>
              <div className='contact-modal__input'>
                <input
                  id='cm-phone'
                  required
                  type='tel'
                  inputMode='numeric'
                  name='phone'
                  placeholder='0300-1234567'
                  value={phone}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className='contact-modal__group'>
              <label className='contact-modal__label' htmlFor='cm-whatsApp'>
                {t("viewPropDetails.whatsApp")}
              </label>
              <div className='contact-modal__input'>
                <input
                  id='cm-whatsApp'
                  type='tel'
                  inputMode='numeric'
                  name='whatsApp'
                  placeholder='0300-1234567'
                  value={whatsApp}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className='contact-modal__group'>
              <label className='contact-modal__label' htmlFor='cm-email'>
                {t("viewPropDetails.email")}
              </label>
              <div className='contact-modal__input'>
                <input
                  id='cm-email'
                  type='email'
                  name='email'
                  value={email}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className='contact-modal__group'>
              <label className='contact-modal__label' htmlFor='cm-message'>
                {t("viewPropDetails.message")}
              </label>
              <div className='contact-modal__input'>
                <textarea
                  id='cm-message'
                  required
                  name='message'
                  maxLength='150'
                  value={message}
                  onChange={handleChange}
                />
              </div>
            </div>

            <button
              type='submit'
              className='contact-modal__submit'
              disabled={contactLoading}
            >
              {contactLoading ? (
                <Spinner as='span' animation='border' size='sm' role='status' />
              ) : (
                <>
                  {t("viewPropDetails.send")}
                  <i className='bx bx-right-arrow-alt'></i>
                </>
              )}
            </button>
            <p className={`contact-modal__terms ${rtl}`}>
              {(termsContact || termsContactUrdu) &&
              i18n.languages[0] === "urdu"
                ? termsContactUrdu || termsContact
                : "By pressing Send, you agree that PkPlots and its affiliates, and real estate professionals may call/text you about your inquiry."}
            </p>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );

  return (
    <Modal.Body style={{ overflowY: "hidden", scrollbarWidth: "thin" }}>
      <div
        className='container-fluid'
        style={{ height: "100vh", paddingLeft: "0", paddingRight: "0" }}
      >
        <div className='row twoCol' style={{ height: "100%" }}>
          <div
            className={`${
              moveContent ? "col-lg-7-mobile" : "col-lg-7"
            } col-md-7`}
            style={{
              overflowY: "auto",
              paddingLeft: "0",
              paddingRight: "0",
              zIndex: "100",
            }}
          >
            <div
              className='propertyImagesModal'
              style={{ position: "relative" }}
            >
              {listing.images && listing.images.length ? (
                <PropertyImagesCarousel images={listing.images} />
              ) : null}
            </div>
          </div>
          <div
            className='col-lg-5 col-md-5'
            style={{
              height: "100%",
              padding: "0",
              position: "relative",
            }}
          >
            <ListingInfoPanel
              listing={listing}
              videoId={videoId}
              moveContent={moveContent}
              onToggleMoveContent={() => setMoveContent(!moveContent)}
              contactSlot={contactSlot}
            />
          </div>
        </div>
      </div>
    </Modal.Body>
  );
};

export default ListingDetailsPage;
